/**
 * Cart Cross-Sell Section v3.0.8 FINAL
 * Musicmedia24 - 03.01.2026 09:30 CET
 * Status: ✅ PRODUKTIV
 * 
 * 5-Level Fallback System:
 * 1. Shopify Recommendations API (complementary)
 * 2. Related Products (same collection) ← FUNKTIONIERT!
 * 3. Vendor/Interpret Match (same artist)
 * 4. Genre Mapping (manual mapping)
 * 5. Bestseller Fallback (from collection)
 */

console.log('🚀 Cart Cross-Sell v3.0.8 FINAL loaded!');

class CartCrossSell {
  constructor() {
    this.container = document.querySelector('[data-cart-cross-sell]');
    if (!this.container) {
      console.log('🛒 Cart Cross-Sell: Container not found');
      return;
    }

    // Configuration
    this.config = {
      recommendationIntent: 'complementary',
      maxRecommendations: 4,
      debugMode: true,
      reloadDelay: 800,
      toastDuration: 3000,
      filterTechnicalCollections: true,
      maxBadges: 4
    };

    this.mappingData = this.loadMappingData();
    this.cartItems = this.getCartItems();
    this.init();
  }

  loadMappingData() {
    const scriptTag = document.querySelector('[data-cart-mapping]');
    if (!scriptTag) {
      this.log('⚠️ Cart mapping data not found', null, 'warn');
      return { genre_mapping: {}, product_tags: [], cart_count: 0, cart_total: '0' };
    }
    
    try {
      const data = JSON.parse(scriptTag.textContent);
      this.log('✅ Mapping data loaded:', data);
      return data;
    } catch (e) {
      this.log('❌ Failed to parse cart mapping data:', e, 'error');
      return { genre_mapping: {}, product_tags: [], cart_count: 0, cart_total: '0' };
    }
  }

  getCartItems() {
    const items = Array.from(document.querySelectorAll('[data-cart-item]')).map(item => ({
      id: item.dataset.productId,
      handle: item.dataset.productHandle,
      vendor: item.dataset.productVendor || '',
      tags: (item.dataset.productTags || '').split(',').map(t => t.trim()).filter(t => t),
      collectionHandles: (item.dataset.collectionHandles || '').split(',').map(h => h.trim()).filter(h => h)
    }));

    this.log(`🛒 Found ${items.length} cart items:`, items);
    return items;
  }

  async init() {
    if (this.cartItems.length === 0) {
      this.log('ℹ️ Cart is empty, hiding section');
      this.hideSection();
      return;
    }

    this.log('🚀 Starting recommendation engine...');
    const recommendations = await this.getRecommendations();
    
    if (recommendations.length > 0) {
      this.log(`✅ Final recommendations (${recommendations.length}):`, recommendations);
      this.renderRecommendations(recommendations);
      this.showSection();
      this.trackAnalytics('cross_sell_shown', { 
        count: recommendations.length,
        cart_count: this.mappingData.cart_count 
      });
    } else {
      this.log('⚠️ No recommendations found, hiding section');
      this.hideSection();
    }
  }

  async getRecommendations() {
    let recommendations = [];
    const excludeIds = this.cartItems.map(item => item.id);

    this.log('🔒 Excluding cart product IDs:', excludeIds);

    // Level 1: Shopify Recommendations API
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('🔍 LEVEL 1: Shopify Recommendations API');
    recommendations = await this.getShopifyRecommendations(excludeIds);
    
    if (recommendations.length >= this.config.maxRecommendations) {
      this.log(`✅ LEVEL 1 SUCCESS: Found ${recommendations.length} products`);
      this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return recommendations.slice(0, this.config.maxRecommendations);
    }
    this.log(`⚠️ Level 1 incomplete: Only ${recommendations.length} products`);

    // Level 2: Related Products
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('🔍 LEVEL 2: Related Products (same collection)');
    const relatedProducts = await this.getRelatedProducts(excludeIds);
    recommendations = this.mergeUnique(recommendations, relatedProducts);
    
    if (recommendations.length >= this.config.maxRecommendations) {
      this.log(`✅ LEVEL 2 SUCCESS: Found ${recommendations.length} products`);
      this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return recommendations.slice(0, this.config.maxRecommendations);
    }
    this.log(`⚠️ Level 2 incomplete: Only ${recommendations.length} products`);

    // Level 3: Vendor Match
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('🔍 LEVEL 3: Vendor/Interpret Match');
    const vendorProducts = await this.getVendorProducts(excludeIds);
    recommendations = this.mergeUnique(recommendations, vendorProducts);
    
    if (recommendations.length >= this.config.maxRecommendations) {
      this.log(`✅ LEVEL 3 SUCCESS: Found ${recommendations.length} products`);
      this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return recommendations.slice(0, this.config.maxRecommendations);
    }
    this.log(`⚠️ Level 3 incomplete: Only ${recommendations.length} products`);

    // Level 4: Genre Mapping
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('🔍 LEVEL 4: Genre Mapping');
    const genreProducts = await this.getGenreProducts(excludeIds);
    recommendations = this.mergeUnique(recommendations, genreProducts);
    
    if (recommendations.length >= this.config.maxRecommendations) {
      this.log(`✅ LEVEL 4 SUCCESS: Found ${recommendations.length} products`);
      this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return recommendations.slice(0, this.config.maxRecommendations);
    }
    this.log(`⚠️ Level 4 incomplete: Only ${recommendations.length} products`);

    // Level 5: Bestseller Fallback
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('🔍 LEVEL 5: Bestseller Fallback');
    const bestsellers = await this.getBestsellers(excludeIds);
    recommendations = this.mergeUnique(recommendations, bestsellers);
    this.log(`✅ LEVEL 5 COMPLETE: Final count ${recommendations.length} products`);
    this.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    return recommendations.slice(0, this.config.maxRecommendations);
  }

  async getShopifyRecommendations(excludeIds) {
    const recommendations = [];
    
    for (const item of this.cartItems) {
      try {
        const url = `/recommendations/products.json?product_id=${item.id}&limit=8&intent=${this.config.recommendationIntent}`;
        this.log(`   📡 Fetching: ${url}`);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          this.log(`   ⚠️ API returned ${response.status} for product ${item.id}`, null, 'warn');
          continue;
        }
        
        const data = await response.json();
        
        if (data.products && data.products.length > 0) {
          this.log(`   ✅ Found ${data.products.length} recommendations for "${item.handle}"`);
          const filtered = data.products.filter(p => !excludeIds.includes(p.id.toString()));
          this.log(`   ✅ After filtering: ${filtered.length} products`);
          recommendations.push(...filtered);
        } else {
          this.log(`   ℹ️ No recommendations for "${item.handle}"`);
        }
      } catch (e) {
        this.log(`   ❌ API error for product ${item.id}:`, e, 'error');
      }
    }

    return this.deduplicateProducts(recommendations);
  }

  async getRelatedProducts(excludeIds) {
    const technicalCollections = ['genos', 'genos2', 'tyros-4', 'tyros-5', 'korg-pa', 'gm2', 'prov-midifile', 'titel-d'];
    
    let collectionHandles = [...new Set(this.cartItems.flatMap(item => item.collectionHandles))];
    
    if (this.config.filterTechnicalCollections) {
      collectionHandles = collectionHandles.filter(handle => !technicalCollections.includes(handle));
      this.log(`   📚 Collections to search (filtered): ${collectionHandles.join(', ')}`);
    } else {
      this.log(`   📚 Collections to search: ${collectionHandles.join(', ')}`);
    }
    
    const products = [];

    for (const handle of collectionHandles) {
      if (!handle) continue;
      
      try {
        const url = `/collections/${handle}/products.json?limit=12`;
        this.log(`   📡 Fetching: ${url}`);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          this.log(`   ⚠️ Collection "${handle}" returned ${response.status}`, null, 'warn');
          continue;
        }
        
        const data = await response.json();
        
        if (data.products) {
          const filtered = data.products.filter(p => !excludeIds.includes(p.id.toString()));
          this.log(`   ✅ Found ${filtered.length} products in "${handle}"`);
          products.push(...filtered);
        }
      } catch (e) {
        this.log(`   ❌ Collection fetch failed for "${handle}":`, e, 'error');
      }
    }

    return this.deduplicateProducts(products);
  }
  async getVendorProducts(excludeIds) {
    const vendors = [...new Set(this.cartItems.map(item => item.vendor).filter(v => v))];
    
    if (vendors.length === 0) {
      this.log('   ℹ️ No vendors found in cart items');
      return [];
    }

    this.log(`   🎤 Vendors to search: ${vendors.join(', ')}`);
    const products = [];

    for (const vendor of vendors) {
      try {
        const searchQuery = encodeURIComponent(`vendor:${vendor}`);
        const url = `/search/suggest.json?q=${searchQuery}&resources[type]=product&resources[limit]=10`;
        this.log(`   📡 Fetching: ${url}`);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          this.log(`   ⚠️ Search returned ${response.status} for vendor "${vendor}"`, null, 'warn');
          continue;
        }
        
        const data = await response.json();
        
        if (data.resources && data.resources.results && data.resources.results.products) {
          const filtered = data.resources.results.products.filter(p => 
            !excludeIds.includes(p.id.toString()) && p.vendor === vendor
          );
          this.log(`   ✅ Found ${filtered.length} products by "${vendor}"`);
          products.push(...filtered);
        } else {
          this.log(`   ℹ️ No products found for vendor "${vendor}"`);
        }
      } catch (e) {
        this.log(`   ❌ Vendor search failed for "${vendor}":`, e, 'error');
      }
    }

    return this.deduplicateProducts(products);
  }

  async getGenreProducts(excludeIds) {
    const genreMapping = this.mappingData.genre_mapping || {};
    const cartTags = this.mappingData.product_tags || [];
    
    this.log(`   🏷️ Cart tags: ${cartTags.join(', ')}`);
    
    const matchedHandles = new Set();
    
    for (const [genre, handles] of Object.entries(genreMapping)) {
      const genreTag = genre.toLowerCase();
      const matchingTags = cartTags.filter(tag => tag.toLowerCase().includes(genreTag));
      
      if (matchingTags.length > 0) {
        this.log(`   ✅ Genre match: "${genre}" (tags: ${matchingTags.join(', ')})`);
        handles.forEach(h => matchedHandles.add(h));
      }
    }

    if (matchedHandles.size === 0) {
      this.log('   ℹ️ No genre matches found');
      return [];
    }

    this.log(`   🎯 Matched ${matchedHandles.size} product handles`);
    const products = [];
    
    for (const handle of matchedHandles) {
      try {
        const url = `/products/${handle}.json`;
        const response = await fetch(url);
        
        if (!response.ok) continue;
        
        const data = await response.json();
        if (data.product && !excludeIds.includes(data.product.id.toString())) {
          products.push(data.product);
        }
      } catch (e) {
        this.log(`   ⚠️ Product fetch failed for "${handle}"`, null, 'warn');
      }
    }

    this.log(`   ✅ Loaded ${products.length} genre products`);
    return products;
  }

  async getBestsellers(excludeIds) {
    this.log('   ⭐ Loading from best-seller collection');
    
    try {
      const response = await fetch('/collections/best-seller/products.json?limit=20');
      if (!response.ok) {
        this.log('   ⚠️ best-seller collection not found', null, 'warn');
        return [];
      }
      
      const data = await response.json();
      if (data.products) {
        const filtered = data.products.filter(p => !excludeIds.includes(p.id.toString()));
        this.log(`   ✅ Loaded ${filtered.length} bestsellers`);
        return filtered;
      }
    } catch (e) {
      this.log('   ❌ Error loading bestsellers:', e, 'error');
    }
    
    return [];
  }

  mergeUnique(arr1, arr2) {
    const merged = [...arr1, ...arr2];
    return this.deduplicateProducts(merged);
  }

  deduplicateProducts(products) {
    const seen = new Set();
    return products.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  renderRecommendations(products) {
    const grid = this.container.querySelector('[data-recommendations-grid]');
    if (!grid) {
      this.log('❌ Recommendations grid not found', null, 'error');
      return;
    }

    this.log(`🎨 Rendering ${products.length} products...`);
    
    const cards = products.map(product => {
      try {
        return this.createProductCard(product);
      } catch (e) {
        this.log(`❌ Failed to create card for product:`, product, 'error');
        this.log(`   Error: ${e.message}`, null, 'error');
        return '';
      }
    }).filter(card => card !== '');

    grid.innerHTML = cards.join('');
    this.attachEventListeners();
    this.log(`✅ Rendered ${cards.length} product cards to DOM`);
  }

  /**
   * Normalize product object from different API responses
   */
  normalizeProduct(product) {
    // Handle nested product object (from /products/xyz.json)
    if (product.product) {
      product = product.product;
    }
    
    // Convert tags to array if string
    let tags = [];
    if (Array.isArray(product.tags)) {
      tags = product.tags;
    } else if (typeof product.tags === 'string') {
      tags = product.tags.split(',').map(t => t.trim()).filter(t => t);
    }
    
    // Ensure required fields exist
    const normalized = {
      id: product.id,
      title: product.title || 'Unbekanntes Produkt',
      handle: product.handle || '',
      product_type: product.product_type || product.type || '',
      vendor: product.vendor || '',
      tags: tags,
      images: product.images || [],
      image: product.image || null,
      variants: product.variants || []
    };
    
    return normalized;
  }

  createProductCard(product) {
    // NORMALIZE PRODUCT FIRST!
    product = this.normalizeProduct(product);
    
    const variant = product.variants[0];
    if (!variant) {
      this.log(`⚠️ No variant found for product: ${product.title}`, null, 'warn');
      return '';
    }
    
    const price = this.formatMoney(variant.price);
    
    // Handle images
    let imageUrl = '';
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      imageUrl = typeof img === 'string' ? img : (img.src || img.url || '');
    } else if (product.image) {
      imageUrl = typeof product.image === 'string' ? product.image : (product.image.src || '');
    }
    
    if (imageUrl) {
      imageUrl = this.optimizeImageUrl(imageUrl, '400x400');
    }
    
    // Extract badges
    const badges = this.extractBadges(product);

    // Generate badge HTML
    let badgeHtml = '';
    if (badges.length > 0) {
      badgeHtml = '<div class="cross-sell-card__badges">';
      badges.forEach(badge => {
        badgeHtml += `<span class="badge badge--${badge.type} badge--${badge.color}">${badge.display}</span>`;
      });
      badgeHtml += '</div>';
    }

    return `
      <div class="cross-sell-card" data-product-id="${product.id}">
        <a href="/products/${product.handle}" class="cross-sell-card__link">
          <div class="cross-sell-card__image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : '<div class="cross-sell-card__no-image">🎵</div>'}
            ${badgeHtml}
          </div>
          <div class="cross-sell-card__info">
            <h3 class="cross-sell-card__title">${this.escapeHtml(product.title)}</h3>
            ${product.vendor ? `<p class="cross-sell-card__vendor">Im Stil von ${this.escapeHtml(product.vendor)}</p>` : ''}
            <p class="cross-sell-card__price">${price}</p>
          </div>
        </a>
        <button class="cross-sell-card__add-btn" data-variant-id="${variant.id}" data-product-title="${this.escapeHtml(product.title)}">
          In den Warenkorb
        </button>
      </div>
    `;
  }

  optimizeImageUrl(url, size = '400x400') {
    if (!url) return '';
    
    let cleanUrl = url.split('?')[0];
    const extension = cleanUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    
    if (extension) {
      cleanUrl = cleanUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/i, `_${size}.$1`);
    }
    
    return cleanUrl;
  }

  /**
   * Extract badges from product
   */
  extractBadges(product) {
    const badges = [];
    const tags = product.tags || [];
    
    // Marketing tags
    const marketingTags = ['Bestseller', 'Trending', 'Neu', 'Sale', 'bestselling'];
    
    // Product type mapping
    const productTypeMapping = {
      'ProV-Midifile': { display: 'ProV', icon: '🎹', color: 'prov' },
      'MIDI File': { display: 'MIDI', icon: '🎹', color: 'midi' },
      'Karaoke': { display: 'Karaoke', icon: '🎤', color: 'karaoke' },
      'MP3': { display: 'MP3', icon: '🎵', color: 'mp3' },
      'Playback': { display: 'Playback', icon: '🎼', color: 'playback' },
      'Noten': { display: 'Noten', icon: '📄', color: 'noten' }
    };
    
    // Format tags (fallback)
    const formatTags = ['GMI', 'GM2', 'KAR', 'MP3', 'GS', 'XG'];
    
    // 1. Marketing tags
    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();
      const matchedMarketing = marketingTags.find(mt => mt.toLowerCase() === lowerTag);
      
      if (matchedMarketing) {
        const displayName = lowerTag === 'bestselling' ? 'Bestseller' : matchedMarketing;
        badges.push({
          name: displayName,
          display: displayName === 'Bestseller' ? '⭐ Bestseller' : 
                   displayName === 'Trending' ? '🔥 Trending' :
                   displayName === 'Neu' ? '✨ Neu' :
                   displayName === 'Sale' ? '💰 Sale' : displayName,
          type: 'marketing',
          color: displayName.toLowerCase(),
          priority: marketingTags.indexOf(matchedMarketing)
        });
      }
    });
    
    // 2. Product type
    if (product.product_type && product.product_type.trim() !== '') {
      const typeConfig = productTypeMapping[product.product_type] || {
        display: product.product_type,
        icon: '🎵',
        color: 'default'
      };
      
      badges.push({
        name: product.product_type,
        display: typeConfig.icon ? `${typeConfig.icon} ${typeConfig.display}` : typeConfig.display,
        type: 'producttype',
        color: typeConfig.color,
        priority: 10
      });
    } else {
      // Fallback: format tags
      tags.forEach(tag => {
        const upperTag = tag.toUpperCase();
        if (formatTags.includes(upperTag)) {
          badges.push({
            name: upperTag,
            display: upperTag,
            type: 'format',
            color: upperTag.toLowerCase(),
            priority: 20 + formatTags.indexOf(upperTag)
          });
        }
      });
    }
    
    // Sort and limit
    const sorted = badges.sort((a, b) => a.priority - b.priority);
    const limited = sorted.slice(0, this.config.maxBadges);
    
    return limited;
  }

  formatMoney(price) {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    if (isNaN(numPrice) || numPrice < 0) {
      this.log(`⚠️ Invalid price: ${price}`, null, 'warn');
      return '0,00 €';
    }
    
    // Shopify prices are in cents (e.g., 699 = 6,99€)
    const euros = numPrice < 100 ? numPrice : numPrice / 100;
    return euros.toFixed(2).replace('.', ',') + ' €';
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  attachEventListeners() {
    const buttons = this.container.querySelectorAll('[data-variant-id]');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
    this.log(`✅ Attached listeners to ${buttons.length} buttons`);
  }

  async handleAddToCart(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;

    button.disabled = true;
    button.textContent = 'Wird hinzugefügt...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          id: variantId, 
          quantity: 1 
        })
      });

      if (response.ok) {
        this.log(`✅ Added to cart: ${productTitle} (variant: ${variantId})`);
        this.showToast(`✓ ${productTitle} wurde hinzugefügt!`);
        this.trackAnalytics('cross_sell_add_to_cart', { 
          variant_id: variantId,
          product_title: productTitle
        });
        
        setTimeout(() => {
          this.log('🔄 Reloading page...');
          window.location.reload();
        }, this.config.reloadDelay);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to cart failed');
      }
    } catch (error) {
      this.log(`❌ Add to cart error: ${error.message}`, null, 'error');
      this.showToast('Fehler beim Hinzufügen', 'error');
      button.disabled = false;
      button.textContent = 'In den Warenkorb';
    }
  }

  showToast(message, type = 'success') {
    document.querySelectorAll('.cart-cross-sell-toast').forEach(t => t.remove());

    const toast = document.createElement('div');
    toast.className = `cart-cross-sell-toast cart-cross-sell-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 100);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, this.config.toastDuration);
  }

  showSection() {
    this.container.style.display = 'block';
    this.log('✅ Section shown');
  }

  hideSection() {
    this.container.style.display = 'none';
    this.log('ℹ️ Section hidden');
  }

  trackAnalytics(event, data = {}) {
    if (window.gtag) {
      window.gtag('event', event, {
        ...data,
        event_category: 'cart_cross_sell',
        event_label: 'Cart Cross-Sell Section'
      });
      this.log(`📊 GA4 tracked: ${event}`, data);
    }
    
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.lib.track(event, data);
      this.log(`📊 Shopify Analytics tracked: ${event}`, data);
    }
  }

  log(message, data = null, level = 'log') {
    if (!this.config.debugMode) return;

    const prefix = '🛒 Cart Cross-Sell:';
    
    if (data !== null) {
      console[level](prefix, message, data);
    } else {
      console[level](prefix, message);
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('🚀 Initializing Cart Cross-Sell Section v3.0.8 FINAL');
    new CartCrossSell();
  });
} else {
  console.log('🚀 Initializing Cart Cross-Sell Section v3.0.8 FINAL (immediate)');
  new CartCrossSell();
}
